package fr.manyverse.app.domain.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Fiche de caractéristiques d'un personnage en mode construction libre.
 *
 * Sérialisée dans la colonne jsonb `characters.sheet` (même approche que `universes.config`).
 * Structure volontairement évolutive : ajouter un nouveau type d'élément = une liste de plus,
 * sans casser le jsonb existant.
 */
@Serializable
data class CharacterSheet(
    @SerialName("gauges")
    val gauges: List<GaugeEntry> = emptyList(),

    @SerialName("characteristics")
    val characteristics: List<CharacteristicEntry> = emptyList(),

    @SerialName("skills")
    val skills: List<SkillEntry> = emptyList(),

    @SerialName("powers")
    val powers: List<PowerEntry> = emptyList(),
)

/** Jauge bornée [min, max] que le joueur fait varier par pas de [increment]. */
@Serializable
data class GaugeEntry(
    @SerialName("id")
    val id: String,

    @SerialName("label")
    val label: String,

    @SerialName("min")
    val min: Int,

    @SerialName("max")
    val max: Int,

    @SerialName("increment")
    val increment: Int,

    @SerialName("current")
    val current: Int,

    /** Couleur ARGB de la barre de progression choisie par le joueur. `null` ⇒ couleur du thème. */
    @SerialName("colorArgb")
    val colorArgb: Long? = null,
)

/** Caractéristique : un libellé libre + une valeur numérique (jusqu'à 12 chiffres ⇒ Long). */
@Serializable
data class CharacteristicEntry(
    @SerialName("id")
    val id: String,

    @SerialName("label")
    val label: String,

    @SerialName("value")
    val value: Long,

    /** Description libre facultative (max 200 caractères) ; `null` ⇒ rien à afficher. */
    @SerialName("description")
    val description: String? = null,
)

/** Compétence : un simple libellé. */
@Serializable
data class SkillEntry(
    @SerialName("id")
    val id: String,

    @SerialName("label")
    val label: String,

    /** Description libre facultative (max 200 caractères) ; `null` ⇒ rien à afficher. */
    @SerialName("description")
    val description: String? = null,
)

/** Pouvoir : un simple libellé, dans une section distincte des compétences. */
@Serializable
data class PowerEntry(
    @SerialName("id")
    val id: String,

    @SerialName("label")
    val label: String,

    /** Description libre facultative (max 200 caractères) ; `null` ⇒ rien à afficher. */
    @SerialName("description")
    val description: String? = null,
)
